import React, { Component } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Popper from '@mui/material/Popper';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import {
  doesStoreLocationValueMatch,
  getStoreLocationKind,
  loadStoreLocationOptions,
  namesFromGrid,
  namesOf,
  readStoreLocationCellValue,
  uniqueNames,
} from './storeLocationFilter';

const DROPDOWN_MIN_WIDTH = 320;

const StoreLocationPopper = (props) => {
  const { style, className, disablePortal, ...other } = props;
  const anchorWidth = style && style.width ? parseFloat(style.width) : 0;
  return (
    <Popper
      {...other}
      disablePortal={Boolean(disablePortal)}
      className={[className, 'ilocal-store-location-popper'].filter(Boolean).join(' ')}
      placement="bottom-start"
      style={{
        ...style,
        zIndex: 13000,
        width: 'auto',
        minWidth: Math.max(anchorWidth || 0, DROPDOWN_MIN_WIDTH),
      }}
    />
  );
};

const readSelected = (model) => {
  if (!model || typeof model !== 'object') return [];
  if (Array.isArray(model.values)) {
    return uniqueNames(model.values);
  }
  if (model.filterType === 'multi' && Array.isArray(model.filterModels)) {
    const setModel = model.filterModels.find(
      (entry) => entry && Array.isArray(entry.values)
    );
    return setModel ? uniqueNames(setModel.values) : [];
  }
  return [];
};

const buildModel = (selected) => {
  const values = uniqueNames(selected);
  if (!values.length) return null;
  return { filterType: 'set', values };
};

export const resolveOptionNames = (kind, api) =>
  loadStoreLocationOptions().then((options) => {
    const fromApi =
      kind === 'store' ? namesOf(options.stores) : namesOf(options.locations);
    const fromGrid = namesFromGrid(api, kind);
    return uniqueNames(fromApi.concat(fromGrid));
  });

const StoreLocationSelect = ({
  options,
  selected,
  onChange,
  onOpen,
  onClose,
  open,
  disablePortal,
  placeholder,
  compact,
}) => (
  <Autocomplete
    multiple
    size="small"
    options={uniqueNames((options || []).concat(selected || []))}
    value={selected}
    open={open}
    onOpen={onOpen}
    onClose={onClose}
    openOnFocus
    autoHighlight
    forcePopupIcon
    disableCloseOnSelect
    limitTags={compact ? 0 : 2}
    disableClearable={false}
    noOptionsText="No matches"
    onChange={(_, next) => onChange(Array.isArray(next) ? next : [])}
    isOptionEqualToValue={(option, value) => option === value}
    renderTags={compact ? () => null : undefined}
    PopperComponent={(popperProps) => (
      <StoreLocationPopper {...popperProps} disablePortal={disablePortal} />
    )}
    renderOption={(props, option, { selected: isSelected }) => (
      <li {...props}>
        <Checkbox size="small" checked={isSelected} sx={{ mr: 1, p: 0.25 }} />
        <span className="ilocal-store-location-option-label">{option}</span>
      </li>
    )}
    ListboxProps={{
      className: 'ilocal-store-location-listbox',
      style: { maxHeight: 280, fontSize: 13 },
    }}
    renderInput={(params) => (
      <TextField
        {...params}
        variant="outlined"
        placeholder={
          selected.length
            ? `${selected.length} selected`
            : placeholder || 'Search'
        }
        onMouseDown={(event) => event.stopPropagation()}
        onClick={(event) => event.stopPropagation()}
      />
    )}
  />
);

class StoreLocationSetFilter extends Component {
  constructor(props) {
    super(props);
    this.selected = readSelected(props.model);
    this.state = {
      selected: this.selected,
      options: [],
      listOpen: false,
    };
    this.notifyTimer = null;
  }

  componentDidMount() {
    this.loadOptions();
  }

  componentWillUnmount() {
    if (this.notifyTimer) clearTimeout(this.notifyTimer);
  }

  destroy() {
    if (this.notifyTimer) {
      clearTimeout(this.notifyTimer);
      this.notifyTimer = null;
    }
    this.setState({ listOpen: false });
  }

  getKind = () =>
    this.props.kind ||
    (this.props.filterParams && this.props.filterParams.kind) ||
    getStoreLocationKind(this.props.colDef) ||
    'store';

  getSelected = () =>
    Array.isArray(this.selected) ? this.selected : this.state.selected || [];

  commitSelected = (selected, after) => {
    this.selected = uniqueNames(selected);
    this.setState({ selected: this.selected }, after);
  };

  loadOptions = () => {
    const custom =
      this.props.filterParams && this.props.filterParams.resolveOptionNames;
    if (typeof custom === 'function') {
      Promise.resolve(
        custom(this.props.api, this.props.colDef, this.props.valueGetter)
      ).then((options) => {
        this.setState({ options });
      });
      return;
    }
    resolveOptionNames(this.getKind(), this.props.api).then((options) => {
      this.setState({ options });
    });
  };

  handleListOpen = () => {
    this.loadOptions();
    this.setState({ listOpen: true });
  };

  handleListClose = () => {
    this.setState({ listOpen: false });
  };

  closeListAfterApply = () => {
    this.setState({ listOpen: false });
  };

  isFilterActive() {
    return this.getSelected().length > 0;
  }

  // Only consulted by client-side grids (e.g. Temperature Monitoring); SSRM
  // reports send the selected names to the API via searchObject instead.
  doesFilterPass(params) {
    const selected = this.getSelected();
    if (!selected.length) return true;
    const value = readStoreLocationCellValue(
      params,
      this.props.colDef,
      this.props.valueGetter
    );
    return doesStoreLocationValueMatch(selected, value);
  }

  getModel() {
    return buildModel(this.getSelected());
  }

  setModel(model) {
    this.commitSelected(readSelected(model));
  }

  getModelAsString(model) {
    const selected = readSelected(model || this.getModel());
    return selected.join(', ');
  }

  setSelectedValues = (selected) => {
    this.commitSelected(selected, this.notifyNow);
  };

  notifyNow = () => {
    if (this.notifyTimer) {
      clearTimeout(this.notifyTimer);
      this.notifyTimer = null;
    }
    this.closeListAfterApply();
    if (this.props.filterChangedCallback) {
      this.props.filterChangedCallback();
    }
  };

  onChange = (selected) => {
    this.commitSelected(selected, () => {
      if (this.notifyTimer) clearTimeout(this.notifyTimer);
      this.notifyTimer = setTimeout(() => {
        this.notifyTimer = null;
        this.notifyNow();
      }, 300);
    });
  };

  render() {
    return (
      <div className="ilocal-store-location-filter ilocal-store-location-filter-popup">
        <StoreLocationSelect
          options={this.state.options}
          selected={this.getSelected()}
          onChange={this.onChange}
          open={this.state.listOpen}
          onOpen={this.handleListOpen}
          onClose={this.handleListClose}
          disablePortal
          placeholder={this.getKind() === 'location' ? 'Location' : 'Store'}
        />
      </div>
    );
  }
}

class StoreLocationFloatingFilter extends Component {
  constructor(props) {
    super(props);
    this.selected = readSelected(props.model || props.currentParentModel);
    this.state = {
      selected: this.selected,
      options: [],
      listOpen: false,
    };
    this.notifyTimer = null;
  }

  componentDidMount() {
    this.loadOptions();
  }

  componentWillUnmount() {
    if (this.notifyTimer) clearTimeout(this.notifyTimer);
  }

  getKind = () =>
    this.props.kind ||
    (this.props.filterParams && this.props.filterParams.kind) ||
    getStoreLocationKind(this.props.colDef) ||
    'store';

  getSelected = () =>
    Array.isArray(this.selected) ? this.selected : this.state.selected || [];

  loadOptions = () => {
    const custom =
      this.props.filterParams && this.props.filterParams.resolveOptionNames;
    if (typeof custom === 'function') {
      Promise.resolve(
        custom(this.props.api, this.props.colDef, this.props.valueGetter)
      ).then((options) => {
        this.setState({ options });
      });
      return;
    }
    resolveOptionNames(this.getKind(), this.props.api).then((options) => {
      this.setState({ options });
    });
  };

  handleListOpen = () => {
    this.loadOptions();
    this.setState({ listOpen: true });
  };

  handleListClose = () => {
    this.setState({ listOpen: false });
  };

  onParentModelChanged(parentModel) {
    this.selected = readSelected(parentModel);
    this.setState({ selected: this.selected });
  }

  getColId = () =>
    (this.props.column &&
      typeof this.props.column.getColId === 'function' &&
      this.props.column.getColId()) ||
    (this.props.colDef && (this.props.colDef.colId || this.props.colDef.field));

  applyViaFilterModel = (model) => {
    const api = this.props.api;
    const colId = this.getColId();
    if (!api || !colId || typeof api.setFilterModel !== 'function') return;
    const current = { ...(api.getFilterModel() || {}) };
    if (model) current[colId] = model;
    else delete current[colId];
    api.setFilterModel(current);
  };

  applyToParent = (selected) => {
    const values = uniqueNames(selected);
    const model = buildModel(values);
    if (typeof this.props.parentFilterInstance === 'function') {
      this.props.parentFilterInstance((instance) => {
        if (instance && typeof instance.setSelectedValues === 'function') {
          instance.setSelectedValues(values);
          return;
        }
        if (instance && typeof instance.setModel === 'function') {
          instance.setModel(model);
          if (typeof instance.notifyNow === 'function') {
            instance.notifyNow();
            return;
          }
        }
        this.applyViaFilterModel(model);
      });
      return;
    }
    this.applyViaFilterModel(model);
  };

  onChange = (selected) => {
    this.selected = uniqueNames(selected);
    this.setState({ selected: this.selected }, () => {
      if (this.notifyTimer) clearTimeout(this.notifyTimer);
      this.notifyTimer = setTimeout(() => {
        this.notifyTimer = null;
        this.applyToParent(this.selected);
        this.setState({ listOpen: false });
      }, 300);
    });
  };

  render() {
    return (
      <div
        className="ilocal-store-location-filter"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <StoreLocationSelect
          compact
          options={this.state.options}
          selected={this.getSelected()}
          onChange={this.onChange}
          open={this.state.listOpen}
          onOpen={this.handleListOpen}
          onClose={this.handleListClose}
          placeholder={this.getKind() === 'location' ? 'Location' : 'Store'}
        />
      </div>
    );
  }
}

export { StoreLocationSetFilter, StoreLocationFloatingFilter };

const hasCustomFilter = (colDef) => {
  const filter = colDef && colDef.filter;
  if (filter == null || filter === true || filter === 'agTextColumnFilter') {
    return false;
  }
  return true;
};

export const withStoreLocationColumnFilter = (colDef = {}) => {
  if (!colDef || colDef._ilocalStoreLocationFilter) return colDef;

  const colId = String((colDef && colDef.colId) || '').toLowerCase();
  if (colId === 'dispensing_location') {
    return {
      ...colDef,
      _ilocalStoreLocationFilter: true,
      filter: StoreLocationSetFilter,
      floatingFilterComponent: StoreLocationFloatingFilter,
      floatingFilterComponentParams: {
        suppressFilterButton: true,
        kind: 'dispensing_location',
        ...(colDef.floatingFilterComponentParams || {}),
      },
      filterParams: {
        kind: 'dispensing_location',
        ...(colDef.filterParams || {}),
      },
      floatingFilter: true,
      menuTabs: ['generalMenuTab', 'columnsMenuTab'],
    };
  }

  const kind = getStoreLocationKind(colDef);
  if (!kind || hasCustomFilter(colDef) || colDef.filter === false) {
    return colDef;
  }

  return {
    ...colDef,
    _ilocalStoreLocationFilter: true,
    filter: StoreLocationSetFilter,
    floatingFilterComponent: StoreLocationFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
      kind,
    },
    filterParams: {
      kind,
    },
    floatingFilter: true,
    menuTabs: ['generalMenuTab', 'columnsMenuTab'],
  };
};

export const applyStoreLocationColumnFilters = (columnDefs) => {
  if (!Array.isArray(columnDefs)) return columnDefs;
  return columnDefs.map((col) => withStoreLocationColumnFilter(col));
};

export default StoreLocationSetFilter;



// WEBPACK FOOTER //
// ./src/components/grid/StoreLocationDropdownFilter.js